import { trackEvent } from '@/utils/analytics'
import { useAppStore } from '@/store'
import { persistedDocType } from '@/store/boardSlice'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { CatalogTable } from '@/routes/DataCatalog/CatalogTable'
import CatalogFilterBar, { DATE_RANGE_DAYS } from '@/routes/DataCatalog/DataCatalogV2/CatalogFilterBar'
import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import type { DocumentRecord } from '@/types/catalog'
import type { DocumentPosition } from '@/service/api'
import { apiService } from '@/service/api'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { getDocumentStatus } from '@/utils/documentStatus'
import { DocumentStatus } from '@/components/StatusTag'

import { ICON_WIDTH, ICON_HEIGHT } from './DocumentPreviewShape'
import { GRID_LAYOUT } from './constants'
import { boardIdsFor, resolveBoardDocRef, selectBoardId } from './utils/resolveBoardDocRef'
import { DIALOG_WIDTH } from '@/components/ui/dialogSize'

const PNID_TYPE = 'pnid'
const PFD_TYPE = 'pfd'

// Valid file types for the filter dropdown
const FILTER_ALL = 'all'
const PNID_SET_FILTER = 'pnid_set'
const FILTER_TYPES = ['document', 'transcript', PNID_TYPE, PFD_TYPE, 'timeseries', PNID_SET_FILTER] as const

// Default sorting configuration
const DEFAULT_SORT = [{ id: 'created_at', desc: true }]

const SEARCH_DEBOUNCE_MS = 300
// Infinite-scroll page size (mirrors the main Data Catalog page — FS-534).
const PAGE_SIZE = 30

type DocumentsModalProps = {
  isOpen: boolean
  onClose: () => void
  onReopen?: () => void
}

/** For PNID/PFD docs that have been processed, the board uses graph_id.
 * Shares resolveBoardDocRef with the Data Catalog "Open in new board" path so
 * both entry points add a P&ID to a board the same way (FS-556). */
const getBoardDocId = (doc: DocumentRecord): string => resolveBoardDocRef(doc).document_id

const DocumentsModal = ({ isOpen, onClose, onReopen }: DocumentsModalProps) => {
  const { t } = useTranslation()
  const {
    updateBoard,
    saveBoard,
    getDocuments,
    board,
    allowedDocumentIds,
    fabricItemIds,
  } = useAppStore()
  const [sorting, setSorting] = useState(DEFAULT_SORT)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(FILTER_ALL)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  // The search string the CURRENT results were fetched for (null until the
  // first page lands). The empty message may only render when the typed
  // search, the debounced search, and this all agree — one condition that
  // closes every timing sliver (pre-fetch frame, debounce window, and the
  // frame where the debounce settles before the reload effect runs).
  const [loadedForSearch, setLoadedForSearch] = useState<string | null>(null)
  const [initialSelectionIds, setInitialSelectionIds] = useState<Set<string>>(new Set())
  const [isInitialized, setIsInitialized] = useState(false)

  // Infinite-scroll state (FS-534 — mirrors the main Data Catalog: rows append
  // as you scroll, no numbered pager). `pageRef` holds the last-loaded 0-indexed
  // page; `paginatedDocs` accumulates across pages.
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [paginatedDocs, setPaginatedDocs] = useState<DocumentRecord[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const pageRef = useRef(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // LOCAL state for selections - prevents BoardCanvas re-renders during modal interaction
  // Only syncs to global state when modal closes
  // Stores board-usable IDs (graph_id for processed PNIDs, doc.id otherwise)
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(new Set())

  // Track doc types for selected docs (needed at close time for analytics + positioning)
  const [selectedDocTypes, setSelectedDocTypes] = useState<Map<string, string>>(new Map())

  // Uploader filter state
  const [selectedUploader, setSelectedUploader] = useState(FILTER_ALL)
  const [organizationUsers, setOrganizationUsers] = useState<Array<[string, string]>>([])

  // Status + creation-date filters (FS-534 — align with Data Catalog's CatalogFilterBar)
  const [selectedStatus, setSelectedStatus] = useState(FILTER_ALL)
  const [selectedCreationDate, setSelectedCreationDate] = useState(FILTER_ALL)

  // Abort controller for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Initialize local selection state when modal opens.
  // Backward compat note: board stores graph doc UUID as `document_id` (set by the old
  // modal via doc.id on graph docs). getBoardDocId() returns `properties.graph_id` which
  // is the same graph doc UUID, so pre-existing boards match correctly — no mismatch.
  useEffect(() => {
    if (isOpen) {
      const { selectedDocuments } = useAppStore.getState()
      const freshSelectedDocs = selectedDocuments as DocumentPosition[]
      const selectedIds = new Set(
        freshSelectedDocs.map(
          (doc: DocumentPosition) => doc.document_id
        )
      )
      setInitialSelectionIds(selectedIds)
      setLocalSelectedIds(selectedIds)

      // Pre-populate selectedDocTypes from existing board selections
      const typeMap = new Map<string, string>()
      freshSelectedDocs.forEach((doc: DocumentPosition) => {
        const id = doc.document_id
        if (doc.doc_type) typeMap.set(id, doc.doc_type)
      })
      setSelectedDocTypes(typeMap)

      setIsInitialized(true)
    } else {
      // Reset state when modal closes
      setIsLoadingDocuments(false)
      setLoadedForSearch(null)
      setInitialSelectionIds(new Set())
      setLocalSelectedIds(new Set())
      setSelectedDocTypes(new Map())
      setIsInitialized(false)
      setSelectedTypeFilter(FILTER_ALL)
      setSelectedUploader(FILTER_ALL)
      setSelectedStatus(FILTER_ALL)
      setSelectedCreationDate(FILTER_ALL)
      setSearchTerm('')
      setDebouncedSearch('')
      pageRef.current = 0
      setLoadingMore(false)
      setPaginatedDocs([])
      setTotalDocuments(0)
    }
  }, [isOpen])

  // Fetch organization users for uploader filter dropdown (only on first modal open)
  useEffect(() => {
    if (!isOpen || organizationUsers.length > 0) return
    let isMounted = true
    const fetchUsers = async () => {
      try {
        const response = await apiService.getOrganizationUsers()
        if (isMounted && response.users) {
          const users: Array<[string, string]> = response.users
            .filter((u: { user_id: string | null }) => u.user_id)
            .map((u: { user_id: string | null; name: string | null; email: string | null }) => [u.user_id, u.name || u.email || u.user_id] as [string, string])
          setOrganizationUsers(users)
        }
      } catch {
        // Fail silently - uploader names will just show as user IDs
      }
    }
    fetchUsers()
    return () => { isMounted = false }
  }, [isOpen, organizationUsers.length])

  // Fetch one 0-indexed page. `append=false` replaces (page 0 / filter change);
  // `append=true` accumulates (infinite scroll). Aborts any in-flight request.
  const fetchPage = useCallback(async (pageNum: number, append: boolean) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    if (append) setLoadingMore(true)
    else setIsLoadingDocuments(true)
    try {
      const sortField = sorting[0]
      const isPnidSetFilter = selectedTypeFilter === PNID_SET_FILTER
      const response = await apiService.listDocsPaginated({
        page_number: pageNum,
        page_size: PAGE_SIZE,
        sort_by: sortField?.id === 'created_at' ? 'upload_started_at' : sortField?.id,
        sort_direction: sortField?.desc ? 'desc' : 'asc',
        search: debouncedSearch || undefined,
        type: isPnidSetFilter
          ? 'pnid'
          : selectedTypeFilter !== FILTER_ALL
            ? selectedTypeFilter
            : undefined,
        user_id: selectedUploader !== FILTER_ALL ? selectedUploader : undefined,
        status_bucket: selectedStatus !== FILTER_ALL ? selectedStatus : undefined,
        created_after:
          selectedCreationDate !== FILTER_ALL && DATE_RANGE_DAYS[selectedCreationDate]
            ? new Date(Date.now() - DATE_RANGE_DAYS[selectedCreationDate] * 86_400_000).toISOString()
            : undefined,
        is_set: isPnidSetFilter || undefined,
        fabric_item_ids: fabricItemIds?.length ? fabricItemIds : undefined,
      }, controller.signal)

      if (!controller.signal.aborted) {
        setPaginatedDocs((prev) => (append ? [...prev, ...response.documents] : response.documents))
        setTotalDocuments(response.total)
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return
      console.error('Failed to fetch documents:', e)
      toast.error(t('board:documents.failed_to_load'))
    } finally {
      if (!controller.signal.aborted) {
        // Settle in finally (success OR error) so a failed fetch can't leave
        // the loading overlay stuck; a superseding fetch skips this (aborted).
        setLoadedForSearch(debouncedSearch)
        setIsLoadingDocuments(false)
        setLoadingMore(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable in production; including it causes test instability with mocked useTranslation
  }, [sorting, debouncedSearch, selectedTypeFilter, selectedUploader, selectedStatus, selectedCreationDate, fabricItemIds])

  // Reload from page 0 when the modal opens or any filter/sort changes.
  useEffect(() => {
    if (!isOpen) return
    pageRef.current = 0
    fetchPage(0, false)
    return () => abortControllerRef.current?.abort()
  }, [isOpen, fetchPage])

  const hasMore = paginatedDocs.length < totalDocuments

  // The rendered results match what the user typed only when the typed
  // search, its debounced copy, and the search the results were fetched for
  // all agree. Anything else is a pending search → loading visual.
  const searchSettled = searchTerm === debouncedSearch && debouncedSearch === loadedForSearch

  const loadMore = useCallback(() => {
    if (isLoadingDocuments || loadingMore || !hasMore) return
    pageRef.current += 1
    fetchPage(pageRef.current, true)
  }, [isLoadingDocuments, loadingMore, hasMore, fetchPage])

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { root: scrollContainerRef.current, rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  // FS-689 review: the server's counts describe the query the SERVER answered.
  // Two cases where that is not what the user is looking at, so they are withheld
  // rather than shown as if they were:
  //   - a fresh fetch is in flight. paginatedDocs/totalDocuments still hold the
  //     previous query's answer, and the table's loading overlay is scoped to the
  //     scroll container, so it does not cover the footer — the old numbers would
  //     be read, and announced by a screen reader, as describing the new query.
  //     This also covers a FAILED fetch, which leaves both values stale for good.
  //   - a client-side row restriction is active (Fabric embed / allowedDocumentIds
  //     below). Those filter rows AFTER the server counted them, so no truthful
  //     total exists on the client: the table could show 0 rows while the server
  //     reported 187.
  // The selected count is client state and always accurate, so it is unconditional.
  const isRowSetRestricted = isFabricEmbedMode() || allowedDocumentIds !== null
  const canReportResults = !isLoadingDocuments && !isRowSetRestricted && totalDocuments > 0

  const modalTitle = t('common:modals.add_documents')
  // Type filter options for CatalogFilterBar ('all' + the modal's supported types).
  const filterTypes = useMemo(() => [FILTER_ALL, ...FILTER_TYPES], [])
  const getDocTypeLabel = useCallback(
    // Dynamic i18n key → cast to `never` per the project's typed-i18next convention.
    (type: string): string => t(`data_catalog:doc_types.${type}` as never),
    [t]
  )
  const documentsColumns = [
    { id: 'name', label: t('data_catalog:file_name') },
    { id: 'type', label: t('data_catalog:type') },
    { id: 'file_size', label: t('data_catalog:file_size') },
    { id: 'status', label: t('common:table_columns.status') },
    { id: 'uploaded_by', label: t('data_catalog:uploaded_by') },
    { id: 'created_at', label: t('data_catalog:date_created') },
  ]

  // Server handles type filtering, search, sort, and graph exclusion.
  // Only client-side filtering needed: Fabric embed scoping.
  const tableData = useMemo(() => {
    // Fabric embed mode: server-side scoping via fabric_item_ids
    if (isFabricEmbedMode()) {
      // fabricItemIds with valid entries = server already filtered → return as-is
      // Otherwise (null, empty, or invalid entries) = show nothing (secure default)
      const hasValidIds = fabricItemIds?.some(id => id);
      return hasValidIds ? paginatedDocs : [];
    }
    // Main app: client-side filtering by allowedDocumentIds
    if (allowedDocumentIds !== null) {
      return paginatedDocs.filter(doc => allowedDocumentIds.includes(doc.id))
    }
    return paginatedDocs
  }, [paginatedDocs, allowedDocumentIds, fabricItemIds])

  // Reverse map: board ID (graph_id) -> display doc ID for current page
  // Used to highlight rows whose board ID is in localSelectedIds
  const graphIdToDocId = useMemo(() => {
    const map = new Map<string, string>()
    paginatedDocs.forEach((doc: DocumentRecord) => {
      // Invert whatever getBoardDocId would produce, rather than reading
      // `properties.graph_id` again. A multi-page P&ID resolves to a page graph
      // from `pnid_graph_ids` and has no scalar graph_id, so keying this map on
      // graph_id left its board id unmapped: the row it belongs to never
      // highlighted and its checkbox never ticked, while the file was still
      // added on close.
      const boardId = getBoardDocId(doc)
      if (boardId !== doc.id) map.set(boardId, doc.id)
    })
    return map
  }, [paginatedDocs])

  // Group multi-page P&ID sets, preserving sort order from tableData
  const { groupedEntries, individualData, orderedEntries } = useMemo(() => {
    const setGroups = new Map<string, DocumentRecord[]>()
    tableData.forEach(doc => {
      const setId = doc.properties.multi_page_set_id
      if (setId && (doc.type === PNID_TYPE || doc.type === PFD_TYPE)) {
        if (!setGroups.has(setId)) setGroups.set(setId, [])
        setGroups.get(setId)!.push(doc)
      }
    })

    const groupSetIds = new Set<string>()
    setGroups.forEach((docs, setId) => {
      if (docs.length > 1) groupSetIds.add(setId)
    })

    const emittedSets = new Set<string>()
    type Entry = { type: 'group'; data: DocumentRecord[]; setId: string } | { type: 'individual'; data: DocumentRecord }
    const ordered: Entry[] = []

    tableData.forEach(doc => {
      const setId = doc.properties.multi_page_set_id
      if (setId && groupSetIds.has(setId)) {
        if (!emittedSets.has(setId)) {
          emittedSets.add(setId)
          ordered.push({ type: 'group', data: setGroups.get(setId)!, setId })
        }
      } else {
        ordered.push({ type: 'individual', data: doc })
      }
    })

    const grouped = ordered.filter(e => e.type === 'group') as Array<{ type: 'group'; data: DocumentRecord[]; setId: string }>
    const individuals = ordered.filter(e => e.type === 'individual').map(e => e.data as DocumentRecord)

    return { groupedEntries: grouped, individualData: individuals, orderedEntries: ordered }
  }, [tableData])

  // Translate localSelectedIds (board IDs) to display doc IDs for CatalogTable highlighting
  const selectedDocumentIds = useMemo(() => {
    return Array.from(localSelectedIds).map(id => graphIdToDocId.get(id) ?? id)
  }, [localSelectedIds, graphIdToDocId])

  // Memoized disabled document IDs (processing documents)
  const disabledDocumentIds = useMemo(
    () =>
      tableData
        .filter((doc) => getDocumentStatus(doc) === DocumentStatus.processing)
        .map((doc) => doc.id),
    [tableData]
  )

  const handleSortChange = useCallback((newSorting: typeof DEFAULT_SORT) => {
    // Sorting is a dependency of the reload effect, so changing it refetches
    // from page 0 automatically (no manual page reset needed — FS-534).
    setSorting(newSorting)
  }, [])

  // Row select handler - updates LOCAL state only (not global store)
  // Translates display doc IDs to board-usable IDs (graph_id for processed PNIDs)
  const handleRowSelect = useCallback((id: string, checked: string | boolean) => {
    const doc = paginatedDocs.find((d) => d.id === id)
    if (!doc) return

    const boardId = getBoardDocId(doc)

    setLocalSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) selectBoardId(next, doc)
      else boardIdsFor(doc).forEach((id) => next.delete(id))
      return next
    })

    // Track the doc type for this board ID (needed at close time).
    // When the ID was swapped to graph_id, store 'graph' so the board
    // preload logic knows this is a graph document (not a downloadable file).
    setSelectedDocTypes(prev => {
      const next = new Map(prev)
      if (checked) {
        const isGraphSwapped = boardId !== doc.id
        if (isGraphSwapped) next.delete(doc.id)
        next.set(boardId, isGraphSwapped ? 'graph' : doc.type)
      } else {
        boardIdsFor(doc).forEach((id) => next.delete(id))
      }
      return next
    })
  }, [paginatedDocs])

  // Select all handler - updates LOCAL state only
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const selectableIds = new Set<string>(localSelectedIds)
      const typeUpdates = new Map<string, string>(selectedDocTypes)
      const addDoc = (doc: DocumentRecord) => {
        if (disabledDocumentIds.includes(doc.id)) return
        const boardId = getBoardDocId(doc)
        // Via selectBoardId, so a legacy id already in the set is replaced
        // rather than joined — otherwise Select All saves two cards for one
        // multi-page drawing.
        selectBoardId(selectableIds, doc)
        const isGraphSwapped = boardId !== doc.id
        if (isGraphSwapped) typeUpdates.delete(doc.id)
        typeUpdates.set(boardId, isGraphSwapped ? 'graph' : doc.type)
      }
      individualData.forEach(addDoc)
      groupedEntries.forEach(group => group.data.forEach(addDoc))
      setLocalSelectedIds(selectableIds)
      setSelectedDocTypes(typeUpdates)
    } else {
      // Deselect only the docs visible on this page; keep cross-page selections
      const visibleBoardIds = new Set<string>()
      // Every id this doc may be held under, not just the resolved one — a
      // legacy multi-page card stored under its raw doc id must come off too.
      const removeDoc = (doc: DocumentRecord) =>
        boardIdsFor(doc).forEach((id) => visibleBoardIds.add(id))
      individualData.forEach(removeDoc)
      groupedEntries.forEach(group => group.data.forEach(removeDoc))
      setLocalSelectedIds(prev => {
        const next = new Set(prev)
        visibleBoardIds.forEach(id => next.delete(id))
        return next
      })
      setSelectedDocTypes(prev => {
        const next = new Map(prev)
        visibleBoardIds.forEach(id => next.delete(id))
        return next
      })
    }
  }, [individualData, groupedEntries, disabledDocumentIds, localSelectedIds, selectedDocTypes])

  // Check if document selection has changed since modal opened
  const hasSelectionChanged = useCallback(() => {
    if (localSelectedIds.size !== initialSelectionIds.size) return true
    for (const id of localSelectedIds) {
      if (!initialSelectionIds.has(id)) return true
    }
    return false
  }, [localSelectedIds, initialSelectionIds])

  // Handle modal close - build operations and save directly
  const handleClose = useCallback(() => {
    const shouldSave = isInitialized && hasSelectionChanged() && board

    if (!shouldSave) {
      onClose()
      return
    }

    const {
      selectedDocuments: currentGlobalSelected,
    } = useAppStore.getState()

    // Calculate added and removed document IDs (these are board IDs: graph_id or doc.id)
    const addedIds = Array.from(localSelectedIds).filter(
      (id) => !initialSelectionIds.has(id)
    )
    const removedIds = Array.from(initialSelectionIds).filter(
      (id) => !localSelectedIds.has(id)
    )

    // Track workspace document changes
    if (addedIds.length > 0) {
      trackEvent('workspace-document_added', {
        workspace_id: board.id,
        document_ids: addedIds,
        document_types: addedIds.map((docId) => selectedDocTypes.get(docId) || 'unknown'),
        count: addedIds.length,
      })
    }
    if (removedIds.length > 0) {
      trackEvent('workspace-document_removed', {
        workspace_id: board.id,
        document_ids: removedIds,
        count: removedIds.length,
      })
    }

    // Build the new selectedDocuments array
    let newSelectedDocs = currentGlobalSelected.filter(
      (doc: DocumentPosition) => !removedIds.includes(doc.document_id)
    )

    // Add new documents with calculated positions in a grid layout
    addedIds.forEach((docId) => {
      const docType = selectedDocTypes.get(docId) || 'graph'
      const index = newSelectedDocs.length
      const col = index % GRID_LAYOUT.COLUMNS
      const row = Math.floor(index / GRID_LAYOUT.COLUMNS)
      const x = GRID_LAYOUT.LEFT_OFFSET + col * (ICON_WIDTH + GRID_LAYOUT.MARGIN)
      const y = row * (ICON_HEIGHT + GRID_LAYOUT.MARGIN)
      newSelectedDocs = [
        ...newSelectedDocs,
        {
          document_id: docId,
          x,
          y,
          doc_type: docType,
        },
      ]
    })

    // Update global state
    useAppStore.setState({ selectedDocuments: newSelectedDocs })

    // Refresh the store's documents list so BoardCanvas can resolve names/types.
    // Without this, the documents array stays stale (e.g. empty for a previously
    // empty board), causing the createNodes guard to block shape rendering.
    const allDocIds = newSelectedDocs
      .filter((d: DocumentPosition) => d.doc_type !== 'connector')
      .map((d: DocumentPosition) => d.document_id)
      .filter(Boolean) as string[]
    if (allDocIds.length > 0) {
      getDocuments({ docIds: allDocIds, skipCacheClear: true })
    }

    // Build operations for API call
    const operations = {
      upsert: newSelectedDocs.map((doc: DocumentPosition) => ({
        // live_stream anchors persist as 'timeseries' (matches the
        // link-time attach + LiveStreamDocumentService's own hardcoded
        // link write) so the historian scope sees them. `doc_type` passed
        // EXPLICITLY (review on #25305): DocumentPosition has no `type`
        // field today, so `persistedDocType(doc)` only worked by accident —
        // an interface change adding `type` would silently switch which
        // value gets persisted for every doc type.
        doc_type: persistedDocType({ doc_type: doc.doc_type }),
        document_id: doc.document_id,
        x: doc.x,
        y: doc.y,
      })),
      remove: removedIds,
    }

    updateBoard(operations)
    onClose()

    saveBoard(false).then((success) => {
      if (!success && onReopen) {
        toast(t('board:documents.retry_adding_documents'), {
          action: {
            label: t('board:documents.reopen'),
            onClick: () => onReopen(),
          },
        })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable in production; including it causes test instability with mocked useTranslation
  }, [isInitialized, hasSelectionChanged, board, localSelectedIds, initialSelectionIds, selectedDocTypes, updateBoard, saveBoard, getDocuments, onClose, onReopen])

  return (
    <Modal
      // The modal itself doesn't scroll — the title + filter bar + table header
      // stay pinned and only the table body scrolls (FS-534).
      className={`w-full ${DIALOG_WIDTH.lg} max-h-[80vh] overflow-hidden`}
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
    >
      {/* Flex column so the filter bar + Done stay pinned and only the table
          body flexes/scrolls. Height = modal cap (80vh) minus the dialog chrome
          (title + p-6 padding + gap ≈ 7rem) so the column never overflows the
          modal and clips Done — even when the active-filter chips row appears,
          the table (flex-1) shrinks instead (FS-534). */}
      <div className="flex flex-col min-h-0 max-h-[calc(80vh-7rem)]">
      {/* FS-534: reuse the Data Catalog filter bar (search + Type/Status/Uploader/Date)
          so the board Add Files filters match the main catalog. Upload + bulk-action
          buttons are suppressed (no callbacks, isUploadEnabled=false) — this modal is a
          pure selector. */}
      <div className="shrink-0">
      <CatalogFilterBar
        // The modal's table container has no horizontal padding — align the
        // search input flush with the table's left edge.
        className="px-0"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedType={selectedTypeFilter}
        onTypeChange={setSelectedTypeFilter}
        filterTypes={filterTypes}
        getDocTypeLabel={getDocTypeLabel}
        selectedUploader={selectedUploader}
        onUploaderChange={setSelectedUploader}
        uploaders={organizationUsers}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        selectedCreationDate={selectedCreationDate}
        onCreationDateChange={setSelectedCreationDate}
        onUploadClick={() => {}}
        isUploadEnabled={false}
      />
      </div>
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 border rounded-lg overflow-y-auto relative"
      >
        {/* A typed search counts as loading from the FIRST keystroke: the
            debounce window (searchTerm ahead of debouncedSearch) shows the
            same "Loading…" overlay as the fetch itself, so the user never
            sees a "No Files found" claim (or a blank) mid-search. */}
        {(isLoadingDocuments || !searchSettled) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-dark-card/80 z-20 pointer-events-none">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{t('common:loading')}</span>
            </div>
          </div>
        )}
        <CatalogTable
          cols={documentsColumns}
          groupedEntries={groupedEntries}
          individualData={individualData}
          orderedEntries={orderedEntries}
          // The overlay above is the loading visual — the empty message may
          // only render once loading AND the search debounce have settled and
          // a page has actually arrived.
          showEmptyMessage={!isLoadingDocuments && searchSettled}
          selectable
          stickyHeader
          selectedRows={selectedDocumentIds}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          defaultSort={sorting}
          onSortChange={handleSortChange}
          disabledRows={disabledDocumentIds}
        />
        {/* Infinite-scroll sentinel + bottom loader (FS-534). */}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-px w-full" />}
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('common:loading')}</span>
          </div>
        )}
      </div>
      {/* FS-689: the picker is paginated (PAGE_SIZE rows at a time) but nothing
          said so, and nothing said how many files a filter had actually matched.
          A filename search that matched 187 files looked identical to one that
          matched 30 — so a user who selected everything on screen had no way to
          know there was more to select.

          Shown as two independently verifiable facts rather than one "X of Y
          selected": selection here is additive across filters (FS-625), so a
          combined phrasing would claim the selected files are among the matched
          ones, which is false the moment you select under one filter and then
          switch to another. The overlap is not computable either — an unloaded
          row cannot be checked. `total` is filter-aware for free, since it is
          the count the server returned for the active query. */}
      <div className="shrink-0 flex items-center justify-between gap-4 pt-3">
        {/* aria-atomic: the two halves are sibling spans, so without it a reader
            announces only the one that changed — "12 selected" with no context, or
            a bare result count. The line only makes sense read whole. */}
        <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
          <span>{t('board:documents.selected_count', { count: localSelectedIds.size })}</span>
          {canReportResults && (
            <>
              {' · '}
              <span>
                {hasMore
                  ? t('board:documents.showing_of_total', {
                      // tableData, not paginatedDocs: this is the count of rows
                      // actually rendered below.
                      loaded: tableData.length,
                      total: totalDocuments,
                    })
                  : t('board:documents.match_count', { count: totalDocuments })}
              </span>
            </>
          )}
        </p>
        <Button onClick={handleClose}>
          {t('common:buttons.done')}
        </Button>
      </div>
      </div>
    </Modal>
  )
}

export default DocumentsModal
